import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Badge, Box, Button, CircularProgress, Divider,
    IconButton, List, ListItem, Popover, Snackbar, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
    Notifications as BellIcon,
    Assignment as TicketCreatedIcon,
    Update as StatusChangedIcon,
    ChatBubble as CommentIcon,
    Warning as SlaWarningIcon,
    ErrorOutline as SlaBreachIcon,
    DoneAll as MarkAllReadIcon,
    Close as CloseIcon,
    Psychology as AIBudgetIcon,
} from '@mui/icons-material';
import { RootState, Notification, NotificationType } from '../../types';
import { AppDispatch } from '../../store';
import {
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
} from '../../store/slices/notificationsSlice';
import { getRelativeTimeString } from '../../utils/dateUtils';
import { colorUtils } from '../../theme';

const POLL_INTERVAL_MS = 30_000;

// ── Type config ────────────────────────────────────────────────────────────────

const useTypeConfig = () => {
    const theme = useTheme();
    return {
        ticket_created:       { Icon: TicketCreatedIcon,  color: theme.palette.severity.high,     label: 'New incident'   },
        ticket_status_changed:{ Icon: StatusChangedIcon,  color: theme.palette.primary.main,      label: 'Status update'  },
        ticket_comment:       { Icon: CommentIcon,        color: theme.palette.primary.main,      label: 'New comment'    },
        sla_warning:             { Icon: SlaWarningIcon, color: theme.palette.severity.medium,   label: 'SLA warning'        },
        sla_breach:              { Icon: SlaBreachIcon,  color: theme.palette.severity.critical, label: 'SLA breached'       },
        ai_error_budget_breached:{ Icon: AIBudgetIcon,   color: theme.palette.warning.main,      label: 'AI budget breached' },
    } satisfies Record<NotificationType, { Icon: React.ElementType; color: string; label: string }>;
};

// ── Notification item ──────────────────────────────────────────────────────────

const NotificationItem: React.FC<{
    notification: Notification;
    onRead: (id: string, ticketId: string | null) => void;
}> = ({ notification, onRead }) => {
    const theme = useTheme();
    const config = useTypeConfig()[notification.type];
    const { Icon } = config;
    const isUnread = !notification.read_at;

    return (
        <Box
            onClick={() => onRead(notification.id, notification.ticket_id)}
            sx={{
                display: 'flex',
                gap: 1.5,
                px: 2,
                py: 1.5,
                cursor: notification.ticket_id ? 'pointer' : 'default',
                backgroundColor: isUnread
                    ? colorUtils.withOpacity(theme.palette.primary.main, 0.06)
                    : 'transparent',
                borderLeft: `3px solid ${isUnread ? theme.palette.primary.main : 'transparent'}`,
                transition: 'background 0.15s',
                '&:hover': notification.ticket_id ? {
                    backgroundColor: theme.palette.interactive.hover,
                } : {},
            }}
        >
            {/* Icon bubble */}
            <Box sx={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0, mt: 0.25,
                backgroundColor: colorUtils.withOpacity(config.color, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon sx={{ fontSize: 16, color: config.color }} />
            </Box>

            {/* Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: '0.8rem',
                    fontWeight: isUnread ? 600 : 400,
                    color: theme.palette.text.primary,
                    lineHeight: 1.4,
                }}>
                    {config.label}
                </Typography>
                {notification.body && (
                    <Typography noWrap sx={{
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                        mt: 0.25,
                    }}>
                        {notification.body}
                    </Typography>
                )}
                <Typography sx={{
                    fontSize: '0.68rem',
                    color: theme.palette.text.tertiary,
                    mt: 0.5,
                }}>
                    {getRelativeTimeString(notification.created_at)}
                </Typography>
            </Box>

            {/* Unread dot */}
            {isUnread && (
                <Box sx={{
                    width: 7, height: 7, borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    flexShrink: 0, alignSelf: 'center',
                }} />
            )}
        </Box>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface NotificationBellProps {
    collapsed: boolean;
    ticketBasePath?: string;
    /** Optional override: called instead of navigate when a notification is clicked. */
    onTicketNavigate?: (ticketId: string) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ collapsed, ticketBasePath = '/client/tickets', onTicketNavigate }) => {
    const theme = useTheme();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { notifications, isLoading } = useSelector((state: RootState) => state.notifications);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [toast, setToast] = useState<Notification | null>(null);
    // Track the newest seen notification id to detect arrivals across polls
    const lastSeenIdRef = useRef<string | null>(null);

    const unreadCount = notifications.filter(n => !n.read_at).length;

    // Initial fetch + 30-second polling; detect new notifications and toast them
    useEffect(() => {
        const poll = async () => {
            const result = await dispatch(fetchNotifications());
            if (fetchNotifications.fulfilled.match(result)) {
                const fetched: Notification[] = Array.isArray(result.payload)
                    ? result.payload
                    : (result.payload as any)?.notifications ?? [];

                // Find the newest unread notification
                const unread = fetched.filter(n => !n.read_at);
                if (unread.length === 0) return;

                const newest = unread.reduce((a, b) =>
                    new Date(a.created_at) > new Date(b.created_at) ? a : b
                );

                // Toast it if it's new since last poll
                if (newest.id !== lastSeenIdRef.current) {
                    if (lastSeenIdRef.current !== null) {
                        // Only toast after the first load (not on mount)
                        setToast(newest);
                    }
                    lastSeenIdRef.current = newest.id;
                }
            }
        };

        poll();
        const interval = setInterval(poll, POLL_INTERVAL_MS);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') poll();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [dispatch]);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleRead = (id: string, ticketId: string | null) => {
        dispatch(markNotificationRead(id));
        if (ticketId) {
            if (onTicketNavigate) {
                onTicketNavigate(ticketId);
            } else {
                navigate(`${ticketBasePath}/${ticketId}`);
            }
            handleClose();
        }
    };

    const tooltipSlotProps = {
        popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] },
        tooltip: {
            sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.border.main}`,
                fontSize: '0.8rem', fontWeight: 500,
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            },
        },
        arrow: {
            sx: {
                color: theme.palette.background.paper,
                '&::before': { border: `1px solid ${theme.palette.border.main}` },
            },
        },
    };

    // The trigger row — same visual pattern as nav items
    const bellRow = (
        <Box
            onClick={handleOpen}
            sx={{
                minHeight: 48,
                px: collapsed ? 0 : 2.5,
                mx: collapsed ? 0.5 : 1,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: collapsed ? 0 : 3,
                cursor: 'pointer',
                color: theme.palette.text.secondary,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    backgroundColor: theme.palette.interactive.hover,
                    color: theme.palette.text.primary,
                    transform: collapsed ? 'scale(1.1)' : 'translateX(4px)',
                },
            }}
        >
            <Badge
                badgeContent={unreadCount || null}
                max={99}
                sx={{
                    '& .MuiBadge-badge': {
                        backgroundColor: theme.palette.severity.critical,
                        color: '#fff',
                        fontSize: '0.58rem',
                        fontWeight: 700,
                        minWidth: 16,
                        height: 16,
                        padding: '0 3px',
                        top: 2,
                        right: 2,
                    },
                }}
            >
                <BellIcon sx={{ fontSize: 24 }} />
            </Badge>

            {!collapsed && (
                <>
                    <Typography sx={{
                        flex: 1,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        letterSpacing: '0.02em',
                        color: 'inherit',
                    }}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Box sx={{
                            minWidth: 18, height: 18, borderRadius: '9px',
                            backgroundColor: theme.palette.severity.critical,
                            color: '#fff',
                            fontSize: '0.6rem', fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            px: 0.5,
                        }}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );

    const typeConfig = useTypeConfig();

    return (
        <>
            {collapsed ? (
                <Tooltip title="Notifications" placement="right" arrow slotProps={tooltipSlotProps}>
                    {/* Tooltip needs a single forwardRef child; Box satisfies this */}
                    <Box component="div">{bellRow}</Box>
                </Tooltip>
            ) : (
                bellRow
            )}

            {/* Toast for incoming notifications */}
            <Snackbar
                open={!!toast}
                autoHideDuration={6000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {toast ? (
                    <Box
                        onClick={() => {
                            if (toast.ticket_id) {
                                dispatch(markNotificationRead(toast.id));
                                if (onTicketNavigate) {
                                    onTicketNavigate(toast.ticket_id);
                                } else {
                                    navigate(`${ticketBasePath}/${toast.ticket_id}`);
                                }
                            }
                            setToast(null);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.border.main}`,
                            borderLeft: `4px solid ${typeConfig[toast.type].color}`,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                            cursor: toast.ticket_id ? 'pointer' : 'default',
                            maxWidth: 340,
                        }}
                    >
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                            backgroundColor: colorUtils.withOpacity(typeConfig[toast.type].color, 0.12),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {React.createElement(typeConfig[toast.type].Icon, {
                                sx: { fontSize: 16, color: typeConfig[toast.type].color },
                            })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.palette.text.primary }}>
                                {typeConfig[toast.type].label}
                            </Typography>
                            {toast.body && (
                                <Typography noWrap sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary, mt: 0.25 }}>
                                    {toast.body}
                                </Typography>
                            )}
                        </Box>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setToast(null); }}
                            sx={{ color: theme.palette.text.tertiary, flexShrink: 0 }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Box>
                ) : undefined}
            </Snackbar>

            {/* Notification popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        width: 360,
                        maxHeight: 500,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        ml: 1,
                    },
                }}
            >
                {/* Header */}
                <Box sx={{
                    px: 2, py: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.border.light}`,
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: theme.palette.text.primary }}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            onClick={() => dispatch(markAllNotificationsRead())}
                            startIcon={<MarkAllReadIcon sx={{ fontSize: '14px !important' }} />}
                            sx={{
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                px: 1, py: 0.25,
                                minWidth: 0,
                                '&:hover': {
                                    backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.08),
                                },
                            }}
                        >
                            Mark all read
                        </Button>
                    )}
                </Box>

                {/* Body */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {isLoading && notifications.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                            <BellIcon sx={{ fontSize: 36, color: theme.palette.text.disabled, mb: 1.5, display: 'block', mx: 'auto' }} />
                            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.tertiary }}>
                                No notifications yet
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {notifications.map((n, idx) => (
                                <React.Fragment key={n.id}>
                                    <ListItem disablePadding>
                                        <NotificationItem notification={n} onRead={handleRead} />
                                    </ListItem>
                                    {idx < notifications.length - 1 && (
                                        <Divider sx={{ borderColor: theme.palette.border.light }} />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default NotificationBell;
